import type { SxProps, Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { LIMITED_DIGITAL_PRESENCE_FINDING_ID } from './digitalPresenceFindingPresentation';
import type { ReviewReasonSeverity } from './reviewReason.types';

export const COMMUTE_DISTANCE_FINDING_ID = 'commute-distance-review';
export { LIMITED_DIGITAL_PRESENCE_FINDING_ID };

export type FindingVisualTheme = {
  accent: string;
  accentSoft: string;
  borderColor: string;
  headerBg: string;
  chipLabel: string;
  /** MUI chip color when no custom chipStyle — prefer chipFg/chipBg for themed findings */
  chipColor: 'error' | 'warning' | 'info';
  chipFg: string;
  chipBg: string;
  linkColor: string;
  severityLabel: string;
};

const COMMUTE_LIGHT = {
  accent: '#DC2626',
  accentSoft: '#FEE2E2',
  borderColor: 'rgba(220, 38, 38, 0.42)',
  headerBg: 'rgba(220, 38, 38, 0.07)',
  chipFg: '#B91C1C',
  chipBg: '#FEE2E2',
  linkColor: '#B91C1C',
};

const COMMUTE_DARK = {
  accent: '#F87171',
  accentSoft: 'rgba(248, 113, 113, 0.14)',
  borderColor: 'rgba(248, 113, 113, 0.38)',
  headerBg: 'rgba(248, 113, 113, 0.1)',
  chipFg: '#FCA5A5',
  chipBg: 'rgba(248, 113, 113, 0.18)',
  linkColor: '#FCA5A5',
};

const DIGITAL_PRESENCE_LIGHT = {
  accent: '#0d9488',
  accentSoft: '#f0fdfa',
  borderColor: 'rgba(13, 148, 136, 0.34)',
  headerBg: 'rgba(13, 148, 136, 0.07)',
  chipFg: '#0f766e',
  chipBg: '#ccfbf1',
  linkColor: '#0f766e',
};

const DIGITAL_PRESENCE_DARK = {
  accent: '#2dd4bf',
  accentSoft: 'rgba(45, 212, 191, 0.12)',
  borderColor: 'rgba(45, 212, 191, 0.36)',
  headerBg: 'rgba(45, 212, 191, 0.1)',
  chipFg: '#5eead4',
  chipBg: 'rgba(45, 212, 191, 0.16)',
  linkColor: '#5eead4',
};

export function isCommuteDistanceFinding(findingId: string): boolean {
  return findingId === COMMUTE_DISTANCE_FINDING_ID;
}

export function isLimitedDigitalPresenceFinding(findingId: string): boolean {
  return findingId === LIMITED_DIGITAL_PRESENCE_FINDING_ID;
}

export function findingBriefTourTarget(findingId: string): string {
  return `finding-brief-${findingId}`;
}

export function resolveFindingAccent(
  findingId: string,
  severity: ReviewReasonSeverity,
  theme: Theme,
  isLight: boolean,
): string {
  const visual = findingVisualTheme(findingId, severity, isLight);
  if (visual) return visual.accent;
  if (severity === 'BLOCKER') return theme.palette.error.main;
  if (severity === 'CONCERN') return theme.palette.warning.dark;
  return theme.palette.info.main;
}

export function resolveFindingLinkColor(
  findingId: string,
  severity: ReviewReasonSeverity,
  theme: Theme,
  isLight: boolean,
): string {
  const visual = findingVisualTheme(findingId, severity, isLight);
  if (visual) return visual.linkColor;
  return isLight ? theme.palette.primary.dark : theme.palette.primary.light;
}

export function findingVisualTheme(
  findingId: string,
  severity: ReviewReasonSeverity,
  isLight: boolean,
): FindingVisualTheme | null {
  if (isCommuteDistanceFinding(findingId)) {
    const palette = isLight ? COMMUTE_LIGHT : COMMUTE_DARK;
    return {
      ...palette,
      chipLabel: 'Red flag',
      chipColor: 'error',
      severityLabel: severity,
    };
  }

  if (isLimitedDigitalPresenceFinding(findingId)) {
    const palette = isLight ? DIGITAL_PRESENCE_LIGHT : DIGITAL_PRESENCE_DARK;
    return {
      ...palette,
      chipLabel: 'Concern',
      chipColor: 'warning',
      severityLabel: severity,
    };
  }

  return null;
}

/** Coherent chip for themed findings; falls back to MUI semantic chip colors. */
export function findingSeverityChipProps(
  findingId: string,
  severity: ReviewReasonSeverity,
  isLight: boolean,
): { label: string; color: 'error' | 'warning' | 'info'; sx?: SxProps<Theme> } {
  const visual = findingVisualTheme(findingId, severity, isLight);
  if (visual) {
    return {
      label: visual.chipLabel,
      color: visual.chipColor,
      sx: {
        height: 22,
        fontWeight: 800,
        fontSize: '0.75rem',
        borderRadius: '999px',
        color: visual.chipFg,
        bgcolor: visual.chipBg,
        border: '1px solid',
        borderColor: alpha(visual.accent, isLight ? 0.28 : 0.38),
        '& .MuiChip-label': { px: 1 },
      },
    };
  }
  if (severity === 'BLOCKER') return { label: 'Blocker', color: 'error' };
  if (severity === 'CONCERN') return { label: 'Concern', color: 'warning' };
  return { label: 'Info', color: 'info' };
}
